(() => {
  const root = document.querySelector("[data-diamonds], [data-diamond-list]");
  if (!root) return;

  const form = root.querySelector("[data-filter-form]");
  const grid = root.querySelector("[data-results-grid]");
  // Reuse the server-rendered placeholders for subsequent searches.
  const skeletonMarkup = grid.innerHTML;
  const removeSkeletons = () => grid.querySelectorAll("[data-diamond-skeleton]").forEach((node) => node.remove());
  const summary = root.querySelector("[data-results-summary]");
  const empty = root.querySelector("[data-empty-state]");
  const sortSelect = root.querySelector("[data-sort-select]");
  const loadMoreButton = root.querySelector("[data-load-more-button]");
  const detailView = root.querySelector("[data-detail-view]");
  const detailContent = root.querySelector("[data-detail-content]");
  const backLink = root.querySelector("[data-back-to-results]");
  const configuredApiBaseUrl = root.dataset.apiBaseUrl?.replace(/\/$/, "") || "";
  const localApiBaseUrl = "http://localhost:8080";
  const apiBaseUrl = ["localhost", "127.0.0.1"].includes(window.location.hostname)
    ? localApiBaseUrl
    : configuredApiBaseUrl;
  const selectedCurrency = new URLSearchParams(window.location.search).get("currency") || root.dataset.currency || "GBP";
  const selectedCountryCode = root.dataset.countryCode || "";
  const shippingPolicyUrl = root.dataset.shippingPolicyUrl || "";
  const refundPolicyUrl = root.dataset.refundPolicyUrl || "";
  const perPage = Number.parseInt(root.dataset.pageSize || "20", 10);
  const pageSize = Number.isFinite(perPage) && perPage > 0 ? perPage : 20;
  const configuredTurntableSpeed = Number.parseInt(root.dataset.turntableSpeed || "45", 10);
  const turntableSpeed = Number.isFinite(configuredTurntableSpeed) && configuredTurntableSpeed > 0
    ? configuredTurntableSpeed
    : 45;
  const currentSearchParams = new URLSearchParams(window.location.search);
  const detailId = currentSearchParams.get("id");
  const cacheKey = "gracefullyDiamonds.items";
  const resultsStateKey = "gracefullyDiamonds.resultsUrl";
  const detailReturnKey = "gracefullyDiamonds.detailReturn";
  const cart = document.querySelector("cart-notification") || document.querySelector("cart-drawer");
  const messages = {
    resultsError: "We couldn't load diamonds right now. Please try your search again in a moment.",
    detailError: "We couldn't load this diamond right now. Please try again in a moment.",
    cartError: "We couldn't add this diamond right now. Please try again in a moment."
  };
  const noImageSvg = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 800 800">
      <rect width="800" height="800" fill="#eef1f4"/>
      <path d="M400 205 250 330l150 265 150-265-150-125Zm0 54 82 68H318l82-68Zm-86 118h65l18 110-83-110Zm107 0h65l-83 110 18-110Z" fill="#a8b0b8"/>
      <text x="400" y="640" fill="#6f7780" font-family="Arial, sans-serif" font-size="44" font-weight="700" text-anchor="middle">No image</text>
    </svg>
  `;
  const noImageSrc = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(noImageSvg)}`;

  const catalogueItems = new Map();
  let currentPage = 1;
  let totalCount = 0;
  let loadedCount = 0;
  let isLoading = false;
  let hasMore = true;

  const escapeHtml = (value) =>
    String(value ?? "").replace(/[&<>"']/g, (character) => ({
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      '"': "&quot;",
      "'": "&#39;"
    }[character]));

  const formatMoney = (amount, currency = "GBP") => {
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency
      }).format(amount);
    } catch (_) {
      return `${currency} ${amount}`;
    }
  };

  const formatCarat = (value) => {
    if (!Number.isFinite(value)) return "-";
    return `${value.toFixed(2)}ct`;
  };

  const titleCase = (value) =>
    String(value || "")
      .toLowerCase()
      .split(/[\s_-]+/)
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(" ");

  const formatGradeLabel = (value) => {
    const labels = {
      ID: "Ideal",
      EX: "Excellent",
      VG: "Very Good",
      GD: "Good",
      G: "Good",
      F: "Fair",
      P: "Poor"
    };
    const grade = String(value || "").trim().toUpperCase();
    return labels[grade] || value;
  };

  const formatMappedLabel = (value, labels) => {
    const text = String(value || "").trim();
    if (!text) return value;

    return text.replace(/\b[A-Z]{1,3}\b/g, (part) => labels[part] || part);
  };

  const formatFluorescenceLabel = (value) => formatMappedLabel(value, {
    NON: "None",
    FNT: "Faint",
    SLT: "Slight",
    MED: "Medium",
    STG: "Strong",
    VST: "Very Strong"
  });

  const formatGirdleLabel = (value) => formatMappedLabel(value, {
    XTN: "Extremely Thin",
    ETN: "Extremely Thin",
    VTN: "Very Thin",
    STN: "Slightly Thin",
    THN: "Thin",
    MED: "Medium",
    STK: "Slightly Thick",
    THK: "Thick",
    VTK: "Very Thick",
    ETK: "Extremely Thick",
    XTK: "Extremely Thick"
  });

  const isMeaningfulValue = (value) => {
    const text = String(value ?? "").trim();
    return Boolean(text) && !["-", "n/a", "na", "null", "undefined", "unknown"].includes(text.toLowerCase());
  };

  const isMeaningfulLab = (value) => isMeaningfulValue(value) && String(value).trim().toLowerCase() !== "none";

  const formatTextValue = (value) => {
    if (!isMeaningfulValue(value)) return "";
    return String(value).trim();
  };

  const formatAngle = (value) => {
    if (!Number.isFinite(value) || value <= 0) return "";
    return `${value.toFixed(1)}°`;
  };

  const formatDimensionValue = (value) => {
    if (!Number.isFinite(value) || value <= 0 || value >= 100) return "";
    return value.toFixed(2);
  };

  const formatPercentValue = (value) => {
    if (!Number.isFinite(value) || value <= 0) return "";
    return `${value.toFixed(1)}%`;
  };

  const hasValidUrl = (value) => {
    try {
      const url = new URL(String(value || ""));
      return ["http:", "https:"].includes(url.protocol);
    } catch (_) {
      return false;
    }
  };

  const renderRows = (rows, className = "diamond-detail__specs") => {
    const visibleRows = rows.filter(([, value]) => isMeaningfulValue(value));
    if (visibleRows.length === 0) return "";

    return `
      <dl class="${className}">
        ${visibleRows.map(([label, value]) => `
          <div${label === "Measurements" ? ` class="diamond-detail__spec-row--measurements"` : ""}>
            <dt>${escapeHtml(label)}</dt>
            <dd>${value}</dd>
          </div>
        `).join("")}
      </dl>
    `;
  };

  const renderEscapedRows = (rows, className) =>
    renderRows(rows.map(([label, value]) => [label, escapeHtml(value)]), className);

  const buildMeasurements = (diamond) => {
    const length = formatDimensionValue(diamond.length);
    const width = formatDimensionValue(diamond.width);
    const depth = formatDimensionValue(diamond.depth);
    const measurements = [length, width, depth].filter(Boolean);
    if (measurements.length < 2) return "";

    return `${measurements.join(" × ")} mm`;
  };

  const buildCertificateValue = (diamond) => {
    const certificate = formatTextValue(diamond.certificate);
    if (!certificate) return "";

    const certificateUrl = hasValidUrl(diamond.certificateUrl)
      ? diamond.certificateUrl
      : hasValidUrl(diamond.certificateMaskedUrl) ? diamond.certificateMaskedUrl : "";

    if (!certificateUrl) return escapeHtml(certificate);

    return `
      <span class="diamond-detail__certificate">
        <span>${escapeHtml(certificate)}</span>
        <a href="${escapeHtml(certificateUrl)}" target="_blank" rel="noopener">View certificate</a>
      </span>
    `;
  };

  const buildSummary = (diamond) => [
    diamond.color ? `${diamond.color} Colour` : "",
    diamond.clarity ? `${diamond.clarity} Clarity` : "",
    diamond.cut ? `${formatGradeLabel(diamond.cut)} Cut` : "",
    isMeaningfulLab(diamond.lab) ? `${diamond.lab} Certified` : ""
  ].filter(isMeaningfulValue).join(" · ");

  const formatEyeCleanBadge = (value) => {
    const eyeClean = formatTextValue(value);
    if (!eyeClean) return "";
    if (eyeClean.toLowerCase() === "no") return "Not Eye Clean";
    if (/\beye[-\s]?clean\b/i.test(eyeClean)) return eyeClean;
    if (/%$/.test(eyeClean)) return `${eyeClean} Eye Clean`;

    return `Eye Clean: ${eyeClean}`;
  };

  const formatShadeBadge = (value) => {
    const shade = formatTextValue(value);
    if (!shade || !/\bmixed\s+tinge\b/i.test(shade)) return "";

    return shade;
  };

  const buildQualityBadges = (diamond) => [
    { label: formatShadeBadge(diamond.shade), tone: "negative" },
    { label: formatEyeCleanBadge(diamond.eyeClean), tone: String(diamond.eyeClean || "").trim().toLowerCase() === "no" ? "negative" : "" }
  ].filter((badge) => isMeaningfulValue(badge.label));

  const buildTrustItems = (diamond) => {
    const items = [];
    if (isMeaningfulLab(diamond.lab)) items.push(`${escapeHtml(diamond.lab)} Certified`);

    if (diamond.minDeliveryDays > 0 && diamond.maxDeliveryDays > 0) {
      const delivery = diamond.minDeliveryDays === diamond.maxDeliveryDays
        ? `${diamond.minDeliveryDays} business day delivery`
        : `${diamond.minDeliveryDays}-${diamond.maxDeliveryDays} business day delivery`;
      items.push(delivery);
    } else if (shippingPolicyUrl) {
      items.push(`<a href="${escapeHtml(shippingPolicyUrl)}">Delivery information</a>`);
    }

    if (diamond.supplierAcceptsReturns !== false) {
      if (diamond.returnWindowDays > 0) {
        items.push(`${diamond.returnWindowDays}-day returns`);
      } else if (refundPolicyUrl) {
        items.push(`<a href="${escapeHtml(refundPolicyUrl)}">Returns policy</a>`);
      }
    }

    return items;
  };

  const buildTitle = (diamond) => {
    const diamondType = diamond.diamondType === "lab-grown"
      ? "Lab-grown"
      : diamond.diamondType === "natural" ? "Natural" : "Diamond";

    return `${formatCarat(diamond.carat)} ${titleCase(diamond.shape || "Diamond")} ${diamondType}`;
  };

  const buildDetailTitle = (diamond) => {
    const diamondType = diamond.diamondType === "lab-grown" || diamond.labgrown === true
      ? "Lab-Grown Diamond"
      : diamond.diamondType === "natural" || diamond.labgrown === false ? "Natural Diamond" : "Diamond";

    return `${formatCarat(diamond.carat)} ${titleCase(diamond.shape || "Diamond")} ${diamondType}`;
  };

  const buildStateParams = () => {
    const params = new URLSearchParams();
    const formData = new FormData(form);

    for (const [key, value] of formData.entries()) {
      params.append(key, value);
    }

    if (sortSelect?.value) {
      params.set("sort", sortSelect.value);
    }
    params.set("currency", selectedCurrency);
    for (const name of ["color", "clarity", "cut", "polish", "symmetry", "certificate"]) {
      if (!params.has(name)) params.set(name, name === "certificate" ? "none" : "");
    }

    return params;
  };

  const buildDetailUrl = (diamond) =>
    `${window.location.pathname}?id=${encodeURIComponent(diamond.id)}`;

  const isDirectVideoUrl = (url) => /\.(mp4|webm|ogg)(\?|#|$)/i.test(String(url || ""));

  const normalizeFrameIndex = (index, frameCount) => {
    const parsedIndex = Number.parseInt(index, 10);
    if (!Number.isFinite(parsedIndex) || parsedIndex < 0) return 0;
    return Math.min(parsedIndex, Math.max(frameCount - 1, 0));
  };

  const buildV360FrameUrl = (baseUrl, frameIndex) => {
    const base = String(baseUrl || "").replace(/\/$/, "");
    if (!base) return "";
    return `${base}/${frameIndex}.jpg`;
  };

  const hasV360Frames = (diamond) =>
    Boolean(diamond?.v360Url) && Number.parseInt(diamond.v360Frames, 10) > 0;

  const renderV360Turntable = (diamond, title) => {
    const frameCount = Math.min(Number.parseInt(diamond.v360Frames, 10), 256);
    const startIndex = normalizeFrameIndex(diamond.v360Start || 0, frameCount);
    const baseUrl = String(diamond.v360Url || "");

    return `
      <div
        class="diamond-turntable"
        role="img"
        aria-label="${escapeHtml(title)} 360 view"
        tabindex="0"
        data-turntable
        data-frame-base="${escapeHtml(baseUrl)}"
        data-frame-count="${frameCount}"
        data-frame-index="${startIndex}"
      >
        <canvas aria-hidden="true"></canvas>
        <div class="diamond-turntable__loading">Loading 360...</div>
      </div>
    `;
  };

  const renderDiamondMedia = (diamond, title, placeholderClass) => {
    if (hasV360Frames(diamond)) {
      return renderV360Turntable(diamond, title);
    }

    if (diamond.videoUrl) {
      const videoUrl = escapeHtml(diamond.videoUrl);
      const poster = diamond.imageUrl ? ` poster="${escapeHtml(diamond.imageUrl)}"` : "";

      if (isDirectVideoUrl(diamond.videoUrl)) {
        return `<video src="${videoUrl}"${poster} autoplay muted loop playsinline preload="metadata" aria-label="${escapeHtml(title)}"></video>`;
      }

      return `<iframe src="${videoUrl}" title="${escapeHtml(title)}" loading="lazy" scrolling="no" allow="fullscreen"></iframe>`;
    }

    if (diamond.imageUrl) {
      return `<img src="${escapeHtml(diamond.imageUrl)}" alt="${escapeHtml(title)}" loading="lazy">`;
    }

    return `<div class="${placeholderClass}">No media available</div>`;
  };

  const renderCardImage = (diamond, title, isFirstResult) => {
    const imageSrc = diamond.imageUrl || noImageSrc;
    const alt = diamond.imageUrl ? title : `${title} image unavailable`;
    return `<img src="${escapeHtml(imageSrc)}" alt="${escapeHtml(alt)}" loading="${isFirstResult ? "eager" : "lazy"}"${isFirstResult ? ' fetchpriority="high"' : ""} data-fallback-image>`;
  };

  const initCardImageFallbacks = (container) => {
    container.querySelectorAll("img[data-fallback-image]").forEach((image) => {
      if (image.dataset.fallbackInitialized === "true") return;
      image.dataset.fallbackInitialized = "true";

      image.addEventListener("error", () => {
        if (image.src === noImageSrc) return;
        image.src = noImageSrc;
        image.alt = "No image available";
      });
    });
  };

  const initTurntables = (container) => {
    container.querySelectorAll("[data-turntable]").forEach((turntable) => {
      const frameCount = Number.parseInt(turntable.dataset.frameCount || "0", 10);
      const maxFrameIndex = Math.max(frameCount - 1, 0);
      const startIndex = normalizeFrameIndex(turntable.dataset.frameIndex || 0, frameCount);
      const baseUrl = turntable.dataset.frameBase || "";
      const canvas = turntable.querySelector("canvas");
      const loadingMessage = turntable.querySelector(".diamond-turntable__loading");
      const context = canvas?.getContext("2d");
      if (!canvas || !context || !frameCount || !baseUrl) return;

      const effectiveFrameCount = Math.min(maxFrameIndex + 1, 72);
      const frameInterval = Math.max(1, Math.ceil((maxFrameIndex + 1) / effectiveFrameCount));
      const frameIndices = Array.from({ length: effectiveFrameCount }, (_, index) =>
        Math.min(index * frameInterval, maxFrameIndex)
      ).filter((value, index, values) => values.indexOf(value) === index);
      if (!frameIndices.includes(startIndex)) {
        frameIndices.unshift(startIndex);
      }

      const frames = new Map();
      let currentPosition = Math.max(frameIndices.indexOf(startIndex), 0);
      let dragStartX = 0;
      let dragStartPosition = currentPosition;
      let rotateTimer = null;

      const drawFrame = (frameIndex) => {
        const image = frames.get(frameIndex);
        if (!image) return;

        if (canvas.width !== image.naturalWidth || canvas.height !== image.naturalHeight) {
          canvas.width = image.naturalWidth;
          canvas.height = image.naturalHeight;
        }

        context.clearRect(0, 0, canvas.width, canvas.height);
        context.drawImage(image, 0, 0);
        turntable.dataset.frameIndex = String(frameIndex);
      };

      const loadFrame = (frameIndex) =>
        new Promise((resolve) => {
          if (frames.has(frameIndex)) {
            resolve(frames.get(frameIndex));
            return;
          }

          const image = new Image();
          image.onload = () => {
            frames.set(frameIndex, image);
            resolve(image);
          };
          image.onerror = () => resolve(null);
          image.src = buildV360FrameUrl(baseUrl, frameIndex);
        });

      const showPosition = (position) => {
        currentPosition = (position + frameIndices.length) % frameIndices.length;
        drawFrame(frameIndices[currentPosition]);
      };

      loadFrame(startIndex).then((image) => {
        if (!image) return;
        drawFrame(startIndex);
        loadingMessage?.setAttribute("hidden", "");
      });

      frameIndices
        .filter((frameIndex) => frameIndex !== startIndex)
        .forEach((frameIndex) => {
          loadFrame(frameIndex);
        });

      const stopRotate = () => {
        if (!rotateTimer) return;
        window.clearInterval(rotateTimer);
        rotateTimer = null;
      };

      const startRotate = () => {
        if (rotateTimer) return;
        rotateTimer = window.setInterval(() => showPosition(currentPosition + 1), turntableSpeed);
      };

      turntable.addEventListener("pointerenter", (event) => {
        if (event.pointerType === "touch") return;
        startRotate();
      });

      turntable.addEventListener("pointerleave", stopRotate);

      turntable.addEventListener("pointerdown", (event) => {
        stopRotate();
        dragStartX = event.clientX;
        dragStartPosition = currentPosition;
        turntable.setPointerCapture(event.pointerId);
      });

      turntable.addEventListener("pointermove", (event) => {
        if (!turntable.hasPointerCapture(event.pointerId)) return;

        const offset = Math.round((event.clientX - dragStartX) / 6);
        showPosition(dragStartPosition - offset);
      });

      turntable.addEventListener("keydown", (event) => {
        if (!["ArrowLeft", "ArrowRight"].includes(event.key)) return;
        event.preventDefault();

        showPosition(currentPosition + (event.key === "ArrowRight" ? 1 : -1));
      });
    });
  };

  const buildResultsUrl = () => {
    const params = new URLSearchParams(window.location.search);
    for (const name of new Set(appliedParams.keys())) {
      params.delete(name);
      appliedParams.getAll(name).forEach((value) => params.append(name, value));
    }
    params.delete("id");
    const query = params.toString();
    return query ? `${window.location.pathname}?${query}` : window.location.pathname;
  };

  const saveResultsUrl = () => {
    const url = buildResultsUrl();
    try {
      sessionStorage.setItem(resultsStateKey, url);
    } catch (_) {
      // Ignore storage failures and fall back to the bare page path.
    }
    return url;
  };

  const readResultsUrl = () => {
    try {
      return sessionStorage.getItem(resultsStateKey) || "";
    } catch (_) {
      return "";
    }
  };

  const saveDetailReturnState = (diamondId) => {
    try {
      sessionStorage.setItem(detailReturnKey, diamondId);
    } catch (_) {
      // Ignore storage failures and just omit the back link.
    }
  };

  const consumeDetailReturnState = () => {
    try {
      const value = sessionStorage.getItem(detailReturnKey) || "";
      sessionStorage.removeItem(detailReturnKey);
      return value;
    } catch (_) {
      return "";
    }
  };

  const applyStateFromParams = (params) => {
    if (!(params instanceof URLSearchParams)) return;

    const radioFields = ["diamondType", "shape"];
    for (const fieldName of radioFields) {
      const value = params.get(fieldName);
      if (!value) continue;

      const input = form.querySelector(`input[name="${fieldName}"][value="${CSS.escape(value)}"]`);
      if (input) input.checked = true;
    }

    const checkboxFields = ["color", "clarity", "cut", "polish", "symmetry", "certificate"];
    for (const fieldName of checkboxFields) {
      const values = params.getAll(fieldName);
      if (values.length === 0) continue;

      form.querySelectorAll(`input[name="${fieldName}"]`).forEach((input) => {
        input.checked = values.includes(input.value);
      });
    }

    const inputFields = ["minCarat", "maxCarat", "minPrice", "maxPrice"];
    for (const fieldName of inputFields) {
      const value = params.get(fieldName);
      if (value === null) continue;

      const input = form.querySelector(`[name="${fieldName}"]`);
      if (input) input.value = value;
    }

    const sortValue = params.get("sort");
    if (sortValue && sortSelect) {
      const option = Array.from(sortSelect.options).find(({ value }) => value === sortValue);
      if (option) sortSelect.value = sortValue;
    }
  };

  const readCachedDiamonds = () => {
    try {
      return JSON.parse(sessionStorage.getItem(cacheKey) || "{}");
    } catch (_) {
      return {};
    }
  };

  const cacheDiamonds = (items) => {
    if (!Array.isArray(items) || items.length === 0) return;

    const cached = readCachedDiamonds();
    for (const item of items) {
      if (item?.id) cached[item.id] = item;
    }

    try {
      sessionStorage.setItem(cacheKey, JSON.stringify(cached));
    } catch (_) {
      // If storage is unavailable, the details page can still show a helpful fallback.
    }
  };

  const formatDimension = (value) => {
    if (!Number.isFinite(value) || value <= 0) return "-";
    return `${value.toFixed(2)}mm`;
  };

  const formatPercent = (value) => {
    if (!Number.isFinite(value) || value <= 0) return "-";
    return `${value.toFixed(1)}%`;
  };

  const setButtonLoading = (button, isButtonLoading) => {
    if (!button) return;
    if (!isButtonLoading && button.dataset.inBasket === "true") {
      button.disabled = true;
      button.textContent = "In basket";
      return;
    }

    button.disabled = isButtonLoading;
    button.textContent = isButtonLoading ? "Adding..." : "Add to basket";
  };

  const normalizeVariantId = (variantId) =>
    String(variantId || "").replace("gid://shopify/ProductVariant/", "");

  const diamondHandle = (diamondId) =>
    `selected-diamond-${String(diamondId || "").trim().toLowerCase().replace(/[\/_]/g, "-")}`;

  const cartItemMatchesDiamond = (item, diamondId, variantId = "") => {
    const expectedVariantId = normalizeVariantId(variantId);
    const itemVariantId = normalizeVariantId(item?.variant_id || item?.variant?.id);
    const properties = item?.properties || {};

    return (
      (expectedVariantId && itemVariantId === expectedVariantId) ||
      properties._diamond_id === diamondId ||
      item?.handle === diamondHandle(diamondId)
    );
  };

  const fetchCart = async () => {
    const cartUrl = window.routes?.cart_url ? `${window.routes.cart_url.replace(/\/$/, "")}.js` : "/cart.js";
    const response = await fetch(cartUrl, {
      headers: {
        Accept: "application/json"
      }
    });

    if (!response.ok) {
      throw new Error("Unable to check the basket right now.");
    }

    return response.json();
  };

  const cartContainsDiamond = async (diamondId, variantId = "") => {
    const currentCart = await fetchCart();
    return Array.isArray(currentCart.items) && currentCart.items.some((item) => cartItemMatchesDiamond(item, diamondId, variantId));
  };

  const setDiamondButtonsInBasket = (diamondId) => {
    root.querySelectorAll(`[data-add-diamond-id="${CSS.escape(diamondId)}"]`).forEach((button) => {
      button.dataset.inBasket = "true";
      button.disabled = true;
      button.textContent = "In basket";
    });
  };

  const syncDiamondButtonsWithCart = async () => {
    const buttons = Array.from(root.querySelectorAll("[data-add-diamond-id]"));
    if (buttons.length === 0) return;

    try {
      const currentCart = await fetchCart();
      const cartItems = Array.isArray(currentCart.items) ? currentCart.items : [];
      for (const button of buttons) {
        const diamondId = button.dataset.addDiamondId;
        if (diamondId && cartItems.some((item) => cartItemMatchesDiamond(item, diamondId))) {
          setDiamondButtonsInBasket(diamondId);
        }
      }
    } catch (_) {
      // If the cart cannot be checked, leave the buttons active and validate on click.
    }
  };

  const addVariantToCart = async (diamond, variantId, button) => {
    const config = fetchConfig("javascript");
    config.headers["X-Requested-With"] = "XMLHttpRequest";
    delete config.headers["Content-Type"];

    const formData = new FormData();
    formData.append("id", variantId);
    formData.append("quantity", "1");
    formData.append("properties[_diamond_id]", diamond.id);
    formData.append("properties[_diamond_stock_id]", diamond.stockNumber || "");

    if (cart) {
      formData.append(
        "sections",
        cart.getSectionsToRender().map((section) => section.id)
      );
      formData.append("sections_url", window.location.pathname + window.location.search);
      cart.setActiveElement(button);
    }

    config.body = formData;

    const cartResponse = await fetch(`${routes.cart_add_url}`, config);
    return cartResponse.json();
  };

  const addDiamondToCart = async (diamond, button) => {
    if (!diamond) return;

    setButtonLoading(button, true);

    try {
      if (await cartContainsDiamond(diamond.id)) {
        setDiamondButtonsInBasket(diamond.id);
        window.alert("This diamond is already in your basket.");
        return;
      }

      const variantResponse = await fetch(`${apiBaseUrl}/api/diamond-cart`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "true"
        },
        body: JSON.stringify({
          id: diamond.id,
          currency: selectedCurrency,
          countryCode: selectedCountryCode
        })
      });

      const variantPayload = await variantResponse.json().catch(() => null);
      if (!variantResponse.ok) {
        console.error("Diamond cart preparation failed:", variantPayload?.error || variantResponse.statusText);
        throw new Error(messages.cartError);
      }

      const variantId = variantPayload?.variantId;
      if (!variantId) {
        throw new Error("Variant was not returned.");
      }
      if (variantPayload?.fixedMarketPriceError) {
        console.warn("Shopify market fixed price was not applied:", variantPayload.fixedMarketPriceError);
      }

      if (await cartContainsDiamond(diamond.id, variantId)) {
        setDiamondButtonsInBasket(diamond.id);
        window.alert("This diamond is already in your basket.");
        return;
      }

      const cartPayload = await addVariantToCart(diamond, variantId, button);

      if (cartPayload.status) {
        throw new Error(cartPayload.description || cartPayload.message || "Unable to add this diamond right now.");
      }

      setDiamondButtonsInBasket(diamond.id);

      if (!cart) {
        window.location = window.routes.cart_url;
        return;
      }

      cart.renderContents(cartPayload);
    } catch (error) {
      console.error(error);
      window.alert(error.message || messages.cartError);
    } finally {
      setButtonLoading(button, false);
    }
  };

  const renderDetail = (diamond) => {
    if (!detailContent || !detailView) return;

    if (!diamond) {
      detailContent.innerHTML = `
        <div class="diamond-detail diamond-detail--empty">
          <h2>Diamond details unavailable</h2>
          <p>Please return to the results and choose the diamond again.</p>
        </div>
      `;
      detailView.hidden = false;
      return;
    }

    const title = buildDetailTitle(diamond);
    const media = renderDiamondMedia(diamond, title, "diamond-detail__placeholder");
    const summaryText = buildSummary(diamond);
    const qualityBadges = buildQualityBadges(diamond);
    const trustItems = buildTrustItems(diamond);
    const returnWarning = diamond.supplierAcceptsReturns === false
      ? `<p class="diamond-detail__warning">The supplier doesn't allow this item to be returned.</p>`
      : "";
    const mainDetails = [
      ["Shape", escapeHtml(titleCase(diamond.shape))],
      ["Carat", escapeHtml(formatCarat(diamond.carat))],
      ["Colour", escapeHtml(diamond.color)],
      ["Clarity", escapeHtml(diamond.clarity)],
      ["Cut", escapeHtml(formatGradeLabel(diamond.cut))],
      ["Certification", isMeaningfulLab(diamond.lab) ? escapeHtml(diamond.lab) : ""],
      ["Certificate number", buildCertificateValue(diamond)],
      ["Measurements", escapeHtml(buildMeasurements(diamond))]
    ];
    const qualityDetails = [
      ["Polish", formatGradeLabel(diamond.polish)],
      ["Symmetry", formatGradeLabel(diamond.symmetry)],
      ["Fluorescence", formatFluorescenceLabel(diamond.fluorescence)],
      ["Growth Method", diamond.diamondType === "lab-grown" || diamond.labgrown === true ? diamond.labgrownType : ""]
    ];
    const advancedDetails = [
      ["Table %", formatPercentValue(diamond.table)],
      ["Depth %", formatPercentValue(diamond.depthPercent)],
      ["Crown angle", formatAngle(diamond.crownAngle)],
      ["Pavilion angle", formatAngle(diamond.pavilionAngle)],
      ["Girdle", formatGirdleLabel(diamond.girdle)],
      ["Culet", diamond.culet]
    ];
    const mainDetailsHtml = renderRows(mainDetails);
    const qualityDetailsHtml = renderEscapedRows(qualityDetails, "diamond-detail__specs diamond-detail__specs--secondary");
    const advancedDetailsHtml = renderEscapedRows(advancedDetails, "diamond-detail__specs diamond-detail__specs--secondary");

    detailContent.innerHTML = `
      <article class="diamond-detail">
        <div class="diamond-detail__media">${media}</div>
        <div class="diamond-detail__body">
          <p class="diamond-detail__eyebrow">Diamond details</p>
          <h2>${escapeHtml(title)}</h2>
          <p class="diamond-detail__price">${formatMoney(diamond.price || 0, diamond.currency || "GBP")}</p>
          ${summaryText ? `<p class="diamond-detail__summary">${escapeHtml(summaryText)}</p>` : ""}
          ${qualityBadges.length > 0 ? `<div class="diamond-detail__badges">${qualityBadges.map((badge) => `<span${badge.tone ? ` class="is-${escapeHtml(badge.tone)}"` : ""}>${escapeHtml(badge.label)}</span>`).join("")}</div>` : ""}
          <button class="diamond-detail__button diamond-detail__button--primary" type="button" data-add-diamond-id="${escapeHtml(diamond.id)}">Add to basket</button>
          ${trustItems.length > 0 ? `<div class="diamond-detail__trust">${trustItems.map((item) => `<span>${item}</span>`).join("")}</div>` : ""}
          ${returnWarning}
          ${mainDetailsHtml ? `<section class="diamond-detail__section"><h3>Diamond details</h3>${mainDetailsHtml}</section>` : ""}
          ${qualityDetailsHtml ? `<section class="diamond-detail__section"><h3>Additional details</h3>${qualityDetailsHtml}</section>` : ""}
          ${advancedDetailsHtml ? `<section class="diamond-detail__section diamond-detail__section--advanced"><h3>Advanced proportions</h3>${advancedDetailsHtml}</section>` : ""}
          <button class="diamond-detail__button diamond-detail__button--secondary" type="button" data-add-diamond-id="${escapeHtml(diamond.id)}">Add to basket</button>
        </div>
      </article>
    `;
    detailView.hidden = false;
    initTurntables(detailContent);
    syncDiamondButtonsWithCart();
  };

  const loadDiamondDetail = async () => {
    detailContent.innerHTML = `
      <div class="diamond-detail diamond-detail--empty">
        <h2>Loading diamond details...</h2>
      </div>
    `;
    detailView.hidden = false;

    const detailParams = new URLSearchParams({
      id: detailId,
      currency: selectedCurrency
    });
    const response = await fetch(`${apiBaseUrl}/api/diamond?${detailParams}`, {
      headers: {
        Accept: "application/json",
        "ngrok-skip-browser-warning": "true"
      }
    });

    if (!response.ok) {
      const errorPayload = await response.json().catch(() => null);
      console.error("Diamond detail request failed:", errorPayload?.error || response.statusText);
      throw new Error(messages.detailError);
    }

    const diamond = await response.json();
    cacheDiamonds([diamond]);
    return diamond;
  };

  const showDetailPage = async () => {
    root.classList.add("diamonds--detail");
    if (backLink) {
      const returnDiamondId = consumeDetailReturnState();
      const shouldShowBackLink = returnDiamondId === detailId;
      backLink.hidden = !shouldShowBackLink;
      backLink.href = shouldShowBackLink ? (readResultsUrl() || window.location.pathname) : window.location.pathname;
    }
    try {
      renderDetail(await loadDiamondDetail());
    } catch (error) {
      console.error(error);
      detailContent.innerHTML = `
        <div class="diamond-detail diamond-detail--empty">
          <h2>Diamond details unavailable</h2>
          <p>${escapeHtml(messages.detailError)}</p>
        </div>
      `;
      detailView.hidden = false;
    }
  };

  const renderCard = (diamond, isFirstResult = false, position = 1, page = 1) => {
    const title = buildTitle(diamond);
    const media = renderCardImage(diamond, title, isFirstResult);
    const detailUrl = buildDetailUrl(diamond);

    return `
      <article class="diamond-card" data-diamond-id="${escapeHtml(diamond.id)}" data-result-position="${position}" data-result-page="${page}">
        <a class="diamond-card__media" href="${escapeHtml(detailUrl)}" aria-label="${escapeHtml(title)}">${media}</a>
        <div class="diamond-card__body">
          <h2 class="diamond-card__title">
            <a href="${escapeHtml(detailUrl)}">${escapeHtml(title)}</a>
          </h2>
          <p class="diamond-card__meta">
            Col: <strong>${escapeHtml(diamond.color || "-")}</strong>,
            Cla: <strong>${escapeHtml(diamond.clarity || "-")}</strong>,
            Cut: <strong>${escapeHtml(diamond.cut || "-")}</strong>,
            Pol: <strong>${escapeHtml(diamond.polish || "-")}</strong>,
            Sym: <strong>${escapeHtml(diamond.symmetry || "-")}</strong>
          </p>
          <div class="diamond-card__footer">
            <p class="diamond-card__price">${formatMoney(diamond.price || 0, diamond.currency || "GBP")}</p>
            <p class="diamond-card__cert">${escapeHtml(diamond.lab || diamond.certificate || "Certified")}</p>
          </div>
          <a class="diamond-card__view" href="${escapeHtml(detailUrl)}" aria-label="View diamond: ${escapeHtml(title)}">View diamond</a>
          <button class="diamond-card__button" type="button" data-add-diamond-id="${escapeHtml(diamond.id)}">Add to basket</button>
        </div>
      </article>
    `;
  };

  const updateLoadMoreButton = () => {
    if (!loadMoreButton) return;

    loadMoreButton.hidden = loadedCount === 0 || !hasMore;
    loadMoreButton.disabled = isLoading;
    loadMoreButton.textContent = isLoading && loadedCount > 0 ? "Loading more..." : "Load more...";
  };

  const setLoading = (nextIsLoading, isNextPage = false) => {
    isLoading = nextIsLoading;
    root.classList.toggle("is-loading", nextIsLoading);
    root.classList.toggle("is-loading-more", nextIsLoading && isNextPage);
    grid.setAttribute("aria-busy", nextIsLoading ? "true" : "false");
    summary.classList.toggle("is-loading", nextIsLoading && !isNextPage);

    if (nextIsLoading && !isNextPage) {
      if (summary.textContent !== "Loading diamonds...") summary.textContent = "Loading diamonds...";
      if (grid.querySelector(".diamond-card") || !grid.querySelector("[data-diamond-skeleton]")) grid.innerHTML = skeletonMarkup;
      empty.hidden = true;
    }

    if (nextIsLoading && isNextPage) grid.insertAdjacentHTML("beforeend", skeletonMarkup);
    if (!nextIsLoading) removeSkeletons();
    updateLoadMoreButton();
  };

  const updateSummary = () => {
    if (mobile.matches) {
      summary.textContent = `${(totalCount || loadedCount).toLocaleString()} ${activeFilterLabels().length ? "matching diamonds" : "diamonds found"}`;
    } else if (totalCount > 0) {
      summary.textContent = `Showing ${loadedCount} of ${totalCount} diamonds`;
    } else {
      summary.textContent = `${loadedCount} diamonds found`;
    }
  };

  const buildQuery = (page) => {
    const params = new URLSearchParams(appliedParams);
    for (const name of ["color", "clarity", "cut", "polish", "symmetry"]) {
      if (!params.get(name)) params.delete(name);
    }
    params.set("page", String(page));
    params.set("perPage", String(pageSize));
    return params.toString();
  };

  let searchController;
  const loadDiamonds = async ({ reset = false } = {}) => {
    if (!reset && (isLoading || !hasMore)) return;
    searchController?.abort();
    const controller = new AbortController();
    searchController = controller;

    const page = reset ? 1 : currentPage + 1;
    setLoading(true, !reset);

    try {
      const response = await fetch(`${apiBaseUrl}/api/diamonds?${buildQuery(page)}`, {
        signal: controller.signal,
        headers: {
          Accept: "application/json",
          "ngrok-skip-browser-warning": "true"
        }
      });

      if (!response.ok) {
        const errorPayload = await response.json().catch(() => null);
        console.error("Diamond search request failed:", errorPayload?.error || response.statusText);
        throw new Error(messages.resultsError);
      }

      const data = await response.json();
      if (controller.signal.aborted) return;
      const items = Array.isArray(data.items) ? data.items : [];
      cacheDiamonds(items);
      if (reset) catalogueItems.clear();
      items.forEach((diamond) => catalogueItems.set(String(diamond.id), diamond));
      const renderedItems = items.map((diamond, index) => renderCard(diamond, reset && index === 0, (reset ? 0 : loadedCount) + index + 1, page)).join("");

      if (reset) grid.innerHTML = renderedItems;
      else {
        removeSkeletons();
        grid.insertAdjacentHTML("beforeend", renderedItems);
      }
      initCardImageFallbacks(grid);
      syncDiamondButtonsWithCart();
      currentPage = page;
      totalCount = data.totalCount || (reset ? items.length : loadedCount + items.length);
      loadedCount = reset ? items.length : loadedCount + items.length;
      hasMore = items.length === pageSize;

      empty.hidden = loadedCount !== 0;
      updateSummary();
      updateLoadMoreButton();
    } catch (error) {
      if (controller.signal.aborted) return;
      if (reset) {
        grid.innerHTML = "";
        loadedCount = 0;
        totalCount = 0;
        hasMore = false;
        empty.hidden = false;
      } else {
        hasMore = false;
      }

      console.error(error);
      summary.textContent = messages.resultsError;
    } finally {
      if (searchController === controller) setLoading(false);
    }
  };

  form.addEventListener("submit", (event) => {
    event.preventDefault();
    applyFilters();
    closeFilters();
  });

  sortSelect?.addEventListener("change", () => {
    appliedParams.set("sort", sortSelect.value);
    commitFilters();
  });
  loadMoreButton?.addEventListener("click", () => loadDiamonds());
  root.addEventListener("click", (event) => {
    const button = event.target.closest("[data-add-diamond-id]");
    if (!button) return;

    event.preventDefault();
    const diamond = readCachedDiamonds()[button.dataset.addDiamondId];
    addDiamondToCart(diamond, button);
  });
  const handleCardClick = (event) => {
    if (event.type === "auxclick" && event.button !== 1) return;
    const link = event.target.closest(".diamond-card a");
    if (!link) return;

    const diamondId = new URL(link.href, window.location.origin).searchParams.get("id");
    const card = link.closest(".diamond-card");
    const diamond = catalogueItems.get(card.dataset.diamondId);
    if (diamond && typeof window.Shopify?.analytics?.publish === "function") {
      try {
        const publication = window.Shopify.analytics.publish("diamond_card_click", {
          diamond_type: diamond.diamondType,
          carat: diamond.carat,
          shape: diamond.shape,
          colour: diamond.color,
          clarity: diamond.clarity,
          cut: diamond.cut,
          certificate: diamond.lab || diamond.certificate || "",
          price: diamond.price || 0,
          currency: diamond.currency || "GBP",
          result_position: Number(card.dataset.resultPosition),
          page_number: Number(card.dataset.resultPage),
          destination_url: link.href
        });
        publication?.catch?.(() => {});
      } catch (_) {
        // Analytics must never prevent normal link navigation.
      }
    }
    saveResultsUrl();
    if (diamondId) {
      saveDetailReturnState(diamondId);
    }
  };
  root.addEventListener("click", handleCardClick);
  root.addEventListener("auxclick", handleCardClick);

  if (detailId) {
    showDetailPage();
    return;
  }

  const mobile = window.matchMedia("(max-width: 767px)");
  const dialog = root.querySelector("[data-filter-dialog]");
  const filterHome = root.querySelector("[data-filter-home]");
  const openButton = root.querySelector("[data-open-filters]");
  const activeSummary = root.querySelector("[data-active-filters]");
  const defaultParams = buildStateParams();
  let appliedParams = new URLSearchParams(defaultParams);
  let headingFields = new Set(new URLSearchParams(window.location.search).keys());
  const updateHeading = () => {
    const heading = root.querySelector("[data-catalogue-heading]");
    if (!heading) return;
    const type = appliedParams.get("diamondType");
    const parts = [];
    const min = Number(appliedParams.get("minCarat"));
    const max = Number(appliedParams.get("maxCarat"));
    if (headingFields.has("minCarat") || headingFields.has("maxCarat")) {
      if (min > 0 && max >= min && max - min < 0.1 - Number.EPSILON) parts.push(`${min} Carat`);
      else if (min > 0 && max >= min) parts.push(`${min}–${max} Carat`);
      else if (min > 0 && !max) parts.push(`${min}+ Carat`);
      else if (!min && max > 0) parts.push(`Up to ${max} Carat`);
    }
    if (headingFields.has("shape") && appliedParams.get("shape")) parts.push(titleCase(appliedParams.get("shape")));
    if (parts.length || headingFields.has("diamondType")) {
      if (type === "lab-grown") parts.push("Lab-Grown");
      else if (type === "natural") parts.push("Natural");
    }
    heading.textContent = parts.length ? parts.join(" ") + " Diamonds" : "Find your diamond";
  };
  let savedScroll = 0;
  let savedBodyStyle;

  const activeFilterLabels = () => {
    const labels = [];
    for (const name of new Set(defaultParams.keys())) {
      if (["sort", "currency"].includes(name)) continue;
      const values = appliedParams.getAll(name);
      if (values.join(",") === defaultParams.getAll(name).join(",")) continue;
      if (!values.some(Boolean) || values[0] === "none") continue;
      if (["diamondType", "shape"].includes(name)) labels.push(titleCase(values[0]));
      else labels.push(`${({ minCarat: "Min ct", maxCarat: "Max ct", minPrice: "Min price", maxPrice: "Max price", color: "Colour" })[name] || titleCase(name)}: ${values.join(", ")}`);
    }
    return labels;
  };
  const updateActiveSummary = () => {
    updateHeading();
    const labels = activeFilterLabels();
    if (!activeSummary) return;
    activeSummary.textContent = labels.slice(0, 3).join(" · ") + (labels.length > 3 ? ` · +${labels.length - 3} more` : "");
    activeSummary.hidden = labels.length === 0;
  };
  const unlockScroll = () => {
    if (!savedBodyStyle) return;
    Object.assign(document.body.style, savedBodyStyle);
    savedBodyStyle = null;
    window.scrollTo(0, savedScroll);
    openButton.setAttribute("aria-expanded", "false");
  };
  const closeFilters = () => {
    if (!dialog?.open) return;
    dialog.close();
    unlockScroll();
    openButton.focus({ preventScroll: true });
  };
  const placeFilters = () => {
    if (!dialog) return;
    closeFilters();
    if (mobile.matches) dialog.append(form);
    else filterHome.after(form);
    if (!isLoading) updateSummary();
  };
  openButton?.addEventListener("click", () => {
    savedScroll = window.scrollY;
    savedBodyStyle = Object.fromEntries(["position", "top", "width", "overflow"].map((key) => [key, document.body.style[key]]));
    Object.assign(document.body.style, { position: "fixed", top: `-${savedScroll}px`, width: "100%", overflow: "hidden" });
    dialog.showModal();
    openButton.setAttribute("aria-expanded", "true");
    dialog.querySelector("[data-close-filters]").focus();
  });
  dialog?.addEventListener("keydown", (event) => {
    if (event.key !== "Tab") return;
    const first = dialog.querySelector("[data-close-filters]");
    const last = dialog.querySelector("[data-reset-filters]");
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });
  dialog?.addEventListener("cancel", (event) => { event.preventDefault(); closeFilters(); });
  root.querySelector("[data-close-filters]")?.addEventListener("click", closeFilters);
  const commitFilters = () => {
    history.replaceState(null, "", saveResultsUrl());
    updateActiveSummary();
    loadDiamonds({ reset: true });
  };
  const applyFilters = () => {
    const nextParams = buildStateParams();
    for (const name of ["diamondType", "shape", "minCarat", "maxCarat"]) {
      if (nextParams.get(name) !== appliedParams.get(name)) headingFields.add(name);
    }
    appliedParams = nextParams;
    commitFilters();
  };
  root.querySelector("[data-reset-filters]")?.addEventListener("click", () => {
    form.reset();
    sortSelect.value = "price_asc";
    appliedParams = buildStateParams();
    headingFields = new Set();
    commitFilters();
    closeFilters();
  });
  mobile.addEventListener("change", placeFilters);
  window.addEventListener("popstate", () => {
    form.reset();
    sortSelect.value = "price_asc";
    applyStateFromParams(new URLSearchParams(window.location.search));
    appliedParams = buildStateParams();
    headingFields = new Set(new URLSearchParams(window.location.search).keys());
    updateActiveSummary();
    closeFilters();
    loadDiamonds({ reset: true });
  });
  const searchParams = new URLSearchParams(window.location.search);
  applyStateFromParams(searchParams);
  appliedParams = buildStateParams();
  updateActiveSummary();
  placeFilters();
  saveResultsUrl();
  loadDiamonds({ reset: true });
})();
